import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { useHistory } from 'react-router';

import AnalyticsManager from 'ia-react-core/~/services/analytics/AnalyticsManager';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import AnalyticsContext from '../../../components/analytics/#AnalyticsContext';

const TRACKED_ELEMENTS = ['BUTTON', 'A'];
const TRACKED_ROLES = ['button', 'listbox', 'option', 'tab'];
const ANALYTICS_ID_ATTRIBUTE = 'data-analytics-id';
const ANALYTICS_SCOPE_ATTRIBUTE = 'data-analytics-scope';
const ANALYTICS_VALUES_ATTRIBUTE = 'data-analytics-values';

export interface ConfigAnalyticsOptions {
  managers: AnalyticsManager[];
}

export interface ConfigAnalyticsProps { }

const configAnalytics = ({ managers }: ConfigAnalyticsOptions) => (WrappedComponent: FC<ConfigAnalyticsProps>) => {
  const ConfigAnalytics = (props: ConfigAnalyticsProps) => {
    const history = useHistory();

    const { pageValues, values } = useContext(AnalyticsContext);

    const getFlattenedPageValues = useCallback(() => (
      Array.from(pageValues.values()).reduce((result, currentPageValues) => {
        Object.assign(result, currentPageValues);
        return result;
      }, {})
    ), [pageValues]);

    const track = useCallback((data: object) => {
      const flattenedPageValues = getFlattenedPageValues();
      managers.forEach((manager) => manager.track({ ...data, pageValues: flattenedPageValues }));
    }, [getFlattenedPageValues]);

    const pageView = useCallback((data: object) => {
      const flattenedPageValues = getFlattenedPageValues();
      managers.forEach((manager) => manager.pageView({ ...data, pageValues: flattenedPageValues }));
    }, [getFlattenedPageValues]);

    const analytics = useMemo(() => ({
      track,
      pageView,
    }), [pageView, track]);

    const newContext = useMemo(() => ({
      scope: [],
      analytics,
      pageValues,
      values,
    }), [analytics, pageValues, values]);

    const handleClick = useCallback((event: MouseEvent) => {
      const { target } = event as unknown as { target: HTMLInputElement };
      const trackedElement = (
        (
          (
            (
              TRACKED_ELEMENTS.includes(target.tagName) ||
              TRACKED_ROLES.includes(target.getAttribute('role')) ||
              (target.tagName === 'INPUT' && target.getAttribute('type') === 'submit')
            )
            && target
          ) ||
          target.closest(TRACKED_ELEMENTS.join(',')) ||
          target.closest(TRACKED_ROLES.map((role) => `[role="${role}"]`).join(','))
        )
      );

      const isTrackingDisabled = (
        target.getAttribute('data-analytics-disabled') === '' ||
        target.getAttribute('data-analytics-disabled') === 'true' ||
        target.closest('[data-analytics-disabled=""],[data-analytics-disabled="true"]')
      );

      if (trackedElement && !isTrackingDisabled) {
        const id = trackedElement.getAttribute(ANALYTICS_ID_ATTRIBUTE) || trackedElement.getAttribute('data-testid') || trackedElement.id || trackedElement.name;
        const closestScopedElement = target.closest(`[${ANALYTICS_SCOPE_ATTRIBUTE}]`);
        const scope = (closestScopedElement?.getAttribute(ANALYTICS_SCOPE_ATTRIBUTE) || '');
        const currentValue = JSON.parse((closestScopedElement?.getAttribute(ANALYTICS_VALUES_ATTRIBUTE) || '{}'));
        track({ event, id, scope, values: currentValue, pageValues: getFlattenedPageValues() });
      }
    }, [getFlattenedPageValues, track]);

    const delayedPageView = useCallback(() => {
      setTimeout(() => {
        pageView({ location: history.location });
      }, 100);
    }, [history.location, pageView]);

    useEffect(() => {
      delayedPageView();

      const unlisten = history.listen(() => {
        delayedPageView();
      });

      return () => {
        unlisten();
      };
    }, [delayedPageView, history, pageView]);

    useEffect(() => {
      const useCapture = true;
      document.addEventListener('click', handleClick, useCapture);

      return () => {
        document.removeEventListener('click', handleClick, useCapture);
      };
    }, [handleClick]);

    return (
      <AnalyticsContext.Provider value={newContext}>
        <WrappedComponent {...props} />
      </AnalyticsContext.Provider>
    );
  };

  ConfigAnalytics.displayName = getWrappedDisplayName('ConfigAnalytics', WrappedComponent);

  return hoistNonReactStatics(ConfigAnalytics, WrappedComponent);
};

/**
 * @component
 * @visibleName configAnalytics
 */
export default configAnalytics;
